/**
 * Types for the dynamic-forms engine.
 *
 * Two layers, deliberately separated:
 *
 *  1. PRIMITIVE layer — a workflow-agnostic description of a form field.
 *     Knows nothing about the SendMoney workflow (no `serviceProviderFieldId`,
 *     no `optionsSource`, no `relatedFields`, no `datatype` strings). Any data
 *     source — a workflow page, the countries endpoint, a hand-built form — is
 *     expressed as `FieldDescriptor[]` and validated/rendered through the same
 *     primitive code.
 *
 *  2. WORKFLOW layer — the shape the SendMoney endpoints actually return. Only
 *     the workflow adapter reads these; it translates them DOWN into primitives.
 */

import { DisclaimerType } from "../../api/moneyTransfer";

/* -------------------------------------------------------------------------- */
/* PRIMITIVE layer (workflow-agnostic)                                        */
/* -------------------------------------------------------------------------- */

/**
 * The primitive field kinds. Lowercase, and intentionally coarser than the
 * workflow `datatype`s: workflow `Country`, `Dropdown` and `CascadingDropdown`
 * all collapse to `"select"` here — a select is a select. A select whose options
 * depend on other fields carries `optionsUrl`; the workflow distinction (which
 * datatype it was, its cascade relations) stays in the adapter.
 */
export const FIELD_KINDS = ["text", "email", "phone", "select", "integer", "decimal"] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

/** A selectable option for a `select` field. */
export interface FieldOption {
  label: string;
  value: string;
  disabled?: boolean;
}

/** Validation constraints, decoupled from where they came from. */
export interface FieldConstraints {
  required?: boolean;
  /** length bound for text/email/phone number */
  min?: number;
  max?: number;
  /** regex source string (server-owned pattern for text/email/phone) */
  pattern?: string;
  /** numeric value bounds for integer/decimal kinds (distinct from min/max length) */
  minValue?: number;
  maxValue?: number;
  /** message overrides for the numeric bounds (bound-specific copy, e.g. "Minimum amount is 10.00 USD") */
  minValueMessage?: string;
  maxValueMessage?: string;
}

/**
 * The country sub-record carried inside a phone value. Modeled from the
 * endpoint samples: id/code/name/phoneCode/iso are present in the 226 echo;
 * numericCode/isoCode appear in other samples (§4), so optional/nullable.
 */
export interface PhoneCountryRecord {
  id: number;
  code: string;
  name: string;
  phoneCode: string;
  iso: string;
  numericCode?: string | null;
  isoCode?: string;
}

/**
 * Stored value for a `phone` field. `type` is a stray server field (seen as
 * `type: 1` in the 226 echo); tolerated + passed through so the round-trip is
 * lossless.
 */
export interface PhoneValue {
  number: string;
  country: PhoneCountryRecord;
  type?: number;
}

/**
 * A single field, described generically. This is the unit `buildZodSchema`
 * (and, later, `DynamicField`) operate on — never `WorkflowField` directly.
 */
export interface FieldDescriptor {
  /** RHF field key. Caller's choice ("country") or, via the adapter, the serviceProviderFieldId. */
  name: string;
  kind: FieldKind;
  label?: string;
  placeholder?: string;
  /** HTML help text rendered under the control (may contain markup) */
  helpHtml?: string;
  constraints?: FieldConstraints;
  /** for kind: "select" — initial options; replaced when optionsUrl reloads */
  options?: FieldOption[];
  /**
   * select only: options depend on other fields. API-relative URL template whose
   * `{name}` tokens resolve to the current value of the field with that name
   * (e.g. "/moneytransfer/cities/US/{SENDER_ADDRESS_STATE_PROVINCE}/4"). When a
   * referenced field changes, this field's value clears and its options reload.
   */
  optionsUrl?: string;
  readOnly?: boolean;
  value?: unknown;
  /** phone only: country code is preset by the server and can't be changed */
  fixedCountry?: boolean;
}

/** A group of fields with optional intro HTML. Renderer-level grouping, workflow-agnostic. */
export interface SectionDescriptor {
  /** HTML intro rendered above the section's fields (may contain markup) */
  infoHtml?: string;
  fields: FieldDescriptor[];
}

/* -------------------------------------------------------------------------- */
/* WORKFLOW layer (SendMoney endpoint shapes) — read only by the adapter       */
/* -------------------------------------------------------------------------- */

/** The datatypes the workflow server emits. */
export const WORKFLOW_DATATYPES = [
  "Text",
  "Email",
  "PhoneNumber",
  "Country",
  "Dropdown",
  "PhoneNumberFixedCountry",
  "CascadingDropdown",
] as const;
export type WorkflowDatatype = (typeof WORKFLOW_DATATYPES)[number];

/** relatedFields entry — drives cascading (PREDECESSOR) / downstream resets (SUCCESSOR). */
export interface RelatedField {
  type: "PREDECESSOR" | "SUCCESSOR";
  id: string;
}

/** A server validation error attached to a field on a 226 response. */
export interface FieldValidationError {
  errorCode: string;
  errorMessage: string;
  errorDescription: string | null;
}

/**
 * An option as the workflow server sends it. The "--Select--" placeholder
 * arrives with no value (workflow payload) or `value: null` (optionsSource
 * response); the adapter drops it — the select renders its own placeholder.
 */
export interface WorkflowFieldOption {
  label: string;
  value?: string | null;
  disabled?: boolean;
}

/** A field as it arrives in a workflow section (raw `datatype`). */
export interface WorkflowField {
  name: string;
  datatype: string;
  min: number;
  max: number;
  isRequired: boolean;
  isValid: boolean;
  regex?: string;
  isSensitive?: boolean;
  isReadOnly: boolean;
  serviceProviderFieldId: string;
  placeholderText?: string;
  subLabelText?: string;
  moreInfo?: string;
  options?: WorkflowFieldOption[];
  optionsSource?: string;
  relatedFields?: RelatedField[];
  validationError?: FieldValidationError[];
  value?: unknown;
}

/** Response of a field's `optionsSource` endpoint. */
export interface WorkflowOptionsResponse {
  options: WorkflowFieldOption[];
  errors: unknown[];
}

/** Page action button (nextButton on workflow pages, action on the review response). */
export interface WorkflowButton {
  actionType: string;
  displayType: string;
  title: string;
}

/** A section of a workflow page. `info` may contain (malformed) HTML. */
export interface WorkflowSection {
  info?: string;
  fields: WorkflowField[];
}

/** One screen of a server-composed workflow. Ordered by `sequenceId`; `id` is not stable across responses. */
export interface WorkflowPage {
  type: string;
  id: string;
  sequenceId: number;
  title: string;
  subTitle?: string;
  nextButton: WorkflowButton;
  /** not rendered on DynamicForm pages (always empty in samples) */
  displaySections: unknown[];
  sections: WorkflowSection[];
  pageFooter?: {
    content?: string;
    disclaimers?: DisclaimerType[];
  };
}

/** One submitted field value, keyed by serviceProviderFieldId. */
export interface WorkflowFieldValue {
  serviceProviderFieldId: string;
  value: unknown;
}